import { useParams, useNavigate } from 'react-router-dom';
import { useTournament } from '../context/TournamentContext';
import { GameScorecard } from '../components/GameScorecard';
import { StatusBadge } from '../components/LiveIndicator';
import { calculateGameResult } from '../engine/scoring';
import { ArrowLeft } from 'lucide-react';
import type { TeamSide } from '../data/types';

export function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getMatchUp, getTeam, getGamesByMatchUp, getScoresByGame, getPlayer, matchUpResults, upsertScore } = useTournament();

  const matchUp = id ? getMatchUp(id) : undefined;
  if (!matchUp) {
    return <div className="empty-state"><p>Zápas nenalezen</p></div>;
  }

  const teamA = matchUp.team_a_id ? getTeam(matchUp.team_a_id) : null;
  const teamB = matchUp.team_b_id ? getTeam(matchUp.team_b_id) : null;
  const games = getGamesByMatchUp(matchUp.id);
  const result = matchUpResults.get(matchUp.id);

  function getGameLabel(game: typeof games[0]): string {
    if (game.type === 'foursome') return 'Foursome';
    return `Single ${game.game_number}`;
  }

  function getGamePlayersText(game: typeof games[0]): string {
    if (game.type === 'single') {
      const pA = game.player_a_id ? getPlayer(game.player_a_id)?.name : 'TBD';
      const pB = game.player_b_id ? getPlayer(game.player_b_id)?.name : 'TBD';
      return `${pA} vs ${pB}`;
    }
    const a1 = game.foursome_a1_id ? getPlayer(game.foursome_a1_id)?.name : '?';
    const a2 = game.foursome_a2_id ? getPlayer(game.foursome_a2_id)?.name : '?';
    const b1 = game.foursome_b1_id ? getPlayer(game.foursome_b1_id)?.name : '?';
    const b2 = game.foursome_b2_id ? getPlayer(game.foursome_b2_id)?.name : '?';
    return `${a1} + ${a2} vs ${b1} + ${b2}`;
  }

  function handleScoreChange(gameId: string, holeNumber: number, teamSide: TeamSide, strokes: number) {
    upsertScore(gameId, holeNumber, teamSide, strokes);
  }

  return (
    <div>
      <button className="btn btn-ghost mb-md" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Zpět
      </button>

      {/* Header */}
      <div className="card mb-lg" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem', textTransform: 'uppercase', fontWeight: 600 }}>
          {matchUp.label} • {matchUp.scheduled_time}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem' }}>
          <h2 style={{ color: teamA?.color ?? 'var(--side-a)', fontSize: '1.5rem', flex: 1, textAlign: 'right' }}>
            {teamA?.short_name ?? teamA?.name ?? 'TBD'}
          </h2>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '2rem', fontWeight: 800, minWidth: '5rem', textAlign: 'center' }}>
            {result ? `${result.liveTeamAPoints} : ${result.liveTeamBPoints}` : '—'}
          </div>
          <h2 style={{ color: teamB?.color ?? 'var(--side-b)', fontSize: '1.5rem', flex: 1, textAlign: 'left' }}>
            {teamB?.short_name ?? teamB?.name ?? 'TBD'}
          </h2>
        </div>
        <div className="mt-sm">
          {(() => {
            if (result?.isCompleted) return <StatusBadge status="completed" />;
            // Check if ANY game in this matchup has scores
            const hasAnyScores = games.some(g => getScoresByGame(g.id).length > 0);
            if (hasAnyScores) return <StatusBadge status="in_progress" />;
            return <StatusBadge status="scheduled" />;
          })()}
        </div>
      </div>

      {/* Games */}
      {games.map(game => {
        const gameScores = getScoresByGame(game.id);
        const gameResult = calculateGameResult(game, gameScores);

        return (
          <div key={game.id} className="card mb-md">
            <div className="flex items-center justify-between mb-md">
              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>{getGameLabel(game)}</h3>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  {getGamePlayersText(game)}
                </span>
              </div>
              <div className="flex items-center gap-sm">
                {gameResult.matchPlayState.isFinished && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--success)' }}>
                    {gameResult.matchPlayState.resultText}
                  </span>
                )}
                {!gameResult.matchPlayState.isFinished && gameResult.matchPlayState.holesPlayed > 0 && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--live)' }}>
                    {gameResult.matchPlayState.lead === 0 ? 'A/S' : `${gameResult.matchPlayState.lead} UP`}
                  </span>
                )}
                {(() => {
                  const scores = getScoresByGame(game.id);
                  if (gameResult.matchPlayState.isFinished) return <StatusBadge status="completed" />;
                  if (scores.length > 0) return <StatusBadge status="in_progress" />;
                  return <StatusBadge status="scheduled" />;
                })()}
              </div>
            </div>

            <GameScorecard
              game={game}
              onScoreChange={(h, s, st) => handleScoreChange(game.id, h, s, st)}
            />
          </div>
        );
      })}

      {games.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🏌️</div>
          <p>Hry zatím nebyly vytvořeny pro tento zápas</p>
        </div>
      )}
    </div>
  );
}
